import { useEffect, useRef, useState } from 'react';
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MChips, MEmpty, MNote } from '@/components/mobile/MobileKit';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { coursesApi } from '@/api/courses';
import { progressApi, type HomeworkStatus, type HomeworkSubmission } from '@/api/progress';
import { homeworkRowAction, lessonSectionHref, needsRedo, type RowLesson } from '@/lib/homeworkResubmit';
import { homeworkTitleLines } from './homeworkTitles';
import { uploadsApi } from '@/api/uploads';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { useDraftTelemetry } from '@/hooks/useDraftTelemetry';
import { PageHeader } from '@/components/ui/PageHeader';
import { Mascot } from '@/components/ui/Mascot';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { clock } from '@/lib/clock';
import { createRecordingClock } from '@/lib/recordingClock';
import { recordingStarted } from '@/lib/recordingGuard';
import { isUnexpectedStop, pickStopReason, stopReasonKey, type StopFlags, type StopReason } from '@/lib/stopReason';
import { MUTE_GRACE_MS, watchMicrophone, type MicLoss } from '@/lib/micWatch';
import { attachMicLevel, SILENCE_ALERT_MS, type MicMeter } from '@/lib/micLevel';
import { keepScreenAwake } from '@/lib/wakeLock';
import { startBar } from '@/lib/recordingBar';
import { canMeasureAudio, measureBlobSeconds } from '@/lib/audioDuration';
import { recordingVerdict, type RecordingVerdict } from '@/lib/recordingIntegrity';
import { micProblem, micProblemKey } from '@/pages/live/micAccess';
import { isStoreBuild } from '@/lib/platform';
import { APP_DEVICE_NAME } from '@/lib/appBrand';
import { humanizeError } from '@/lib/errors';
import { notifyError, notifySuccess } from '@/lib/haptics';
import { unsafeSendStarted } from '@/lib/recordingGuard';
import { useLeaveWhileRecording } from '@/hooks/useLeaveWhileRecording';
import { useCuratorChat } from '@/hooks/useCuratorChat';
import { reviewNoteOf } from '@/lib/studentFields';
import { useRecordQueue, type RecordQueueView, type SendResult } from '@/hooks/useRecordQueue';
import { canResend, recordForSection } from '@/lib/recordQueue';
import { QueuedOnlyRow, QueuedRecordLine } from './HomeworkQueuedRecord';

// STATUS_TABS is now built inside the component using t() to support i18n.

/** Куда сдаём: id уходит на сервер, названия — на экран ученику. */
type PrefillSection = { id: string; lessonTitle: string; sectionTitle: string };
const EMPTY_PREFILL: PrefillSection = { id: '', lessonTitle: '', sectionTitle: '' };

export function StudentHomeworkPage({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  // «Спросить куратора» — только при праве chat и назначенном кураторе (14.09.2026).
  const { showCuratorEntry } = useCuratorChat();
  // ⚠️ Тот же перехват, что на странице урока, и по той же причине — только
  // здесь он нужнее: запись домашки грузится прямой мутацией, мимо очереди
  // неотправленного, и уход во время загрузки теряет её совсем.
  useLeaveWhileRecording();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<HomeworkStatus | 'all'>('all');
  const [params, setParams] = useSearchParams();
  const [submitOpen, setSubmitOpen] = useState(false);
  // ⚠️ Не один только id. Ученику показывается урок и раздел, а не UUID (10.09.2026),
  // и названия уже лежат в той самой строке, из которой открывают окно, — второго
  // запроса за ними не нужно.
  const [prefill, setPrefill] = useState<PrefillSection>(EMPTY_PREFILL);

  const openSubmit = (hw: HomeworkSubmission) => {
    setPrefill({
      id: hw.section_id,
      lessonTitle: hw.lesson_title ?? '',
      sectionTitle: hw.section_title ?? '',
    });
    setSubmitOpen(true);
  };

  const STATUS_TABS: { value: HomeworkStatus | 'all'; label: string }[] = [
    { value: 'all', label: t('hw.tabAll') },
    { value: 'pending', label: t('hw.tabPending') },
    { value: 'approved', label: t('hw.tabApproved') },
    { value: 'revision', label: t('hw.tabRevision') },
    { value: 'rejected', label: t('hw.tabRejected') },
  ];

  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['student', 'homework', tab],
    queryFn: () => progressApi.listMyHomework(tab === 'all' ? undefined : tab),
  });
  const items = list.data?.items ?? [];

  // «Сдать» — запись голоса, лежащая в очереди к разделу домашки (промпт 22, «да» владельца 18.10.2026). Раздел со
  // сдачей — метка и кнопка в его строке; раздел без сдачи (первая попытка ушла в очередь без связи) — своя строка
  // сверху, только на вкладке «Все». Отправилась — список обновляется: сервер собирает сдачу раздела сам.
  const queue = useRecordQueue();
  const sendRecord = async (id: string): Promise<SendResult> => {
    const res = await queue.sendOne(id);
    if (res === 'sent') void qc.invalidateQueries({ queryKey: ['student', 'homework'] });
    return res;
  };
  const sectionsWithRow = new Set(items.map((h) => h.section_id));
  const queuedOnly: RecordQueueView[] =
    tab === 'all' && !list.isLoading
      ? queue.rows.filter((r) => r.sectionId && !sectionsWithRow.has(r.sectionId) && canResend(r))
      : [];
  const queuedFor = (hw: HomeworkSubmission) => recordForSection(queue.rows, hw.section_id) as RecordQueueView | undefined;

  // ⚠️ «ПЕРЕДЕЛАТЬ» ИЛИ ОКНО — РЕШАЕТ РАЗДЕЛ УРОКА (договор homework-resubmit.md,
  // решение владельца 11.09.2026). Есть в разделе эссе или запись — переделывают в
  // уроке; нет — прежнее окно «Отправить заново». В строке этого знания нет,
  // поэтому урок грузится — только у вернувших работ, по запросу на урок, кэш общий
  // с плеером (тот же ключ).
  const redoLessonIds = [
    ...new Set(items.filter((h) => needsRedo(h) && h.lesson_id).map((h) => h.lesson_id as string)),
  ];
  const redoLessons = useQueries({
    queries: redoLessonIds.map((id) => ({
      queryKey: ['student', 'lesson', id],
      queryFn: () => coursesApi.getStudentLesson(id),
      retry: false,
    })),
  });
  const actionOf = (hw: HomeworkSubmission) => {
    const i = hw.lesson_id ? redoLessonIds.indexOf(hw.lesson_id) : -1;
    const lesson: RowLesson | undefined = i < 0 ? undefined : { status: redoLessons[i].status, data: redoLessons[i].data };
    return homeworkRowAction(hw, lesson);
  };

  // Deep-link: /student/homework?section_id=... auto-opens the submit modal.
  // Used by lesson-section "Сдать ДЗ" buttons in the player.
  useEffect(() => {
    const sid = params.get('section_id');
    if (sid) {
      // Названий здесь взять неоткуда — окно покажет «Раздел урока», но не UUID.
      setPrefill({ id: sid, lessonTitle: '', sectionTitle: '' });
      setSubmitOpen(true);
      params.delete('section_id');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // ── Телефон: домашка одним тонированным блоком ───────────────────────────
  // По макету 23.08.2026: срок — оранжевым (это срочность), состояние — меткой
  // справа, пустой экран объясняет, откуда задания вообще берутся.
  //
  // ⚠️ СТРОКА ДОМАШКИ ЗДЕСЬ НЕ СВОЯ, А ТА ЖЕ, ЧТО НА РАБОЧЕМ СТОЛЕ (10.09.2026).
  // Своя была, и она молча отстала: заголовок в ней был прибит гвоздём — «Домашнее
  // задание» вместо названия урока, — дата шла без локали, а состояние работы, отзыв
  // куратора, оценка и вложения не показывались вовсе. Ради отзыва ученик сюда и
  // заходит. Заказчик прислал снимок 10.09.2026. Пока рендерера два, они расходятся;
  // поэтому он один.
  if (isMobile) {
    return (
      <div className="space-y-3.5">
        <MChips
          chips={STATUS_TABS.map((x) => ({ key: String(x.value), label: x.label }))}
          active={String(tab)}
          onChange={(k) => setTab(k as HomeworkStatus | 'all')}
        />

        {queuedOnly.length > 0 && (
          <MBlock className="px-[14px] py-0.5">
            {queuedOnly.map((r) => (
              <QueuedOnlyRow key={r.id} row={r} onSend={() => sendRecord(r.id)} />
            ))}
          </MBlock>
        )}

        {list.isLoading ? (
          <MBlock className="py-4 text-center text-[13px] text-on-surface-variant">
            {t('common.loading')}
          </MBlock>
        ) : items.length === 0 && queuedOnly.length > 0 ? null : items.length === 0 ? (
          // ⚠️ Кнопки «сдать первое» здесь нет намеренно: домашка рождается в
          // уроке, когда ученик пишет сочинение или наговаривает ответ, а не
          // сдаётся отдельно и без задания (решение заказчика 06.09.2026).
          <MEmpty
            icon="assignment"
            title={tab === 'all' ? t('hw.emptyAll') : t('hw.emptyCategory')}
            hint={t('hw.emptyHint')}
          />
        ) : (
          <MBlock className="px-[14px] py-0.5">
            {items.map((hw) => (
              <HomeworkRow
                key={hw.id}
                hw={hw}
                action={actionOf(hw)}
                onResubmit={() => openSubmit(hw)}
                queued={queuedFor(hw)}
                onSendQueued={sendRecord}
              />
            ))}
          </MBlock>
        )}

        {showCuratorEntry && <MNote icon="chat" title={t('hw.askCurator')} to="/app/chat" />}

        {/* ⚠️ Окно сдачи обязано быть и здесь. Первая версия мобильной ветки
            возвращалась ДО него, и кнопки открывали окно, которого в дереве не
            существовало: домашку с телефона нельзя было сдать вообще. Поймано
            заказчиком 23.08.2026 — «часть функционала отсутствует». */}
        <HomeworkSubmitModal
          open={submitOpen}
          onClose={() => setSubmitOpen(false)}
          defaultSectionId={prefill.id}
          defaultLessonTitle={prefill.lessonTitle}
          defaultSectionTitle={prefill.sectionTitle}
          onSubmitted={() => {
            setSubmitOpen(false);
            qc.invalidateQueries({ queryKey: ['student', 'homework'] });
          }}
        />
      </div>
    );
  }

  const filters = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="flex flex-1 flex-wrap gap-1.5">
        {STATUS_TABS.map((tab_item) => (
          <button
            key={tab_item.value}
            onClick={() => setTab(tab_item.value)}
            className={cn(
              'rounded-full px-3.5 py-[7px] text-[12px] font-semibold transition',
              tab === tab_item.value
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high',
            )}
          >
            {tab_item.label}
          </button>
        ))}
      </div>
      {/* ⚠️ КНОПКИ «СДАТЬ НОВОЕ» ЗДЕСЬ НЕТ, И ЭТО НАМЕРЕННО. Домашка не
          сдаётся отдельно от урока: она РОЖДАЕТСЯ в уроке, когда ученик пишет
          сочинение или наговаривает ответ. Кнопка предлагала сдать работу «в
          воздух», без задания, — и такая сдача не сходилась ни с одним
          разделом. Убрана по решению заказчика 06.09.2026 вместе с «Сдать
          первое ДЗ» в пустом состоянии. */}
    </div>
  );

  return (
    <>
      {!embedded && (
        <PageHeader title={t('hw.title')} subtitle={t('hw.subtitle')} />
      )}

      {filters}

      {queuedOnly.length > 0 && (
        <div className="mb-3 rounded-[20px] bg-surface-container px-[18px] py-0.5">
          {queuedOnly.map((r) => (
            <QueuedOnlyRow key={r.id} row={r} onSend={() => sendRecord(r.id)} />
          ))}
        </div>
      )}

      {list.isLoading ? (
        <div className="h-32 animate-pulse rounded-[20px] bg-surface-container" />
      ) : list.isError ? (
        <div className="space-y-3 rounded-[20px] bg-surface-container px-[18px] py-4 text-center text-on-surface-variant">
          <p className="text-error">{humanizeError(list.error)}</p>
          <Button variant="secondary" size="sm" onClick={() => list.refetch()}>
            {t('hw.retry')}
          </Button>
        </div>
      ) : list.data?.items.length === 0 && queuedOnly.length > 0 ? null : list.data?.items.length === 0 ? (
        // Пустое состояние из макета: рамка пунктиром, маскот, строка и
        // пояснение, откуда здесь берутся задания.
        <div className="flex flex-col items-center gap-[9px] rounded-[22px] border-2 border-dashed border-outline p-6 text-center">
          <Mascot pose="homework" className="mascot h-24 w-auto object-contain" />
          <p className="text-[14px] font-semibold text-on-surface">
            {tab === 'all' ? t('hw.emptyAll') : t('hw.emptyCategory')}
          </p>
          <p className="max-w-[280px] text-[12.5px] text-on-surface-variant">{t('hw.emptyHint')}</p>
        </div>
      ) : (
        <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
          {list.data?.items.map((hw) => (
            <HomeworkRow
              key={hw.id}
              hw={hw}
              action={actionOf(hw)}
              onResubmit={() => openSubmit(hw)}
              queued={queuedFor(hw)}
              onSendQueued={sendRecord}
            />
          ))}
        </div>
      )}

      <HomeworkSubmitModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        defaultSectionId={prefill.id}
        defaultLessonTitle={prefill.lessonTitle}
        defaultSectionTitle={prefill.sectionTitle}
        onSubmitted={() => {
          setSubmitOpen(false);
          qc.invalidateQueries({ queryKey: ['student', 'homework'] });
        }}
      />
    </>
  );
}

/** Значок и его цвет по состоянию работы — как в макете: строка узнаётся сразу. */
const HW_ICON: Record<HomeworkStatus, { icon: string; tone: string }> = {
  pending: { icon: 'schedule', tone: 'bg-primary-container text-primary' },
  approved: { icon: 'check', tone: 'bg-info-container text-info' },
  revision: { icon: 'edit', tone: 'bg-primary-container text-primary' },
  rejected: { icon: 'close', tone: 'bg-surface-container-high text-on-surface-variant' },
};

function HomeworkRow({
  hw,
  action,
  onResubmit,
  queued,
  onSendQueued,
}: {
  hw: HomeworkSubmission;
  action: ReturnType<typeof homeworkRowAction>;
  onResubmit: () => void;
  /** Запись голоса к этому разделу, лежащая в очереди, — метка и «Сдать» под строкой (промпт 22). */
  queued?: RecordQueueView;
  onSendQueued?: (id: string) => Promise<SendResult>;
}) {
  const { t, i18n } = useTranslation();
  // `review_note`, раньше `curator_note` (договор student-api.md §4).
  const reviewNote = reviewNoteOf(hw);

  const STATUS_LABEL: Record<HomeworkStatus, string> = {
    approved: t('hw.statusApproved'),
    pending: t('hw.statusPending'),
    revision: t('hw.statusRevision'),
    rejected: t('hw.statusRejected'),
  };
  const look = HW_ICON[hw.status];
  // Урок крупно, раздел — второй строкой; раздел не повторяет урок, когда названия
  // совпадают. Правило и его сторож — в homeworkTitles.ts.
  const lines = homeworkTitleLines(hw, t('hw.rowTitle'));
  const redoHref = lessonSectionHref(hw);

  return (
    <div data-hw-row={hw.id} className="border-b border-outline py-3 last:border-b-0">
      <div className="flex items-center gap-3">
        <span className={cn('grid h-9 w-9 flex-none place-items-center rounded-xl', look.tone)}>
          <span className="material-symbols-outlined text-[18px]">{look.icon}</span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold text-on-surface">
            {lines.title}
          </span>
          <span
            className={cn(
              'block text-[12px]',
              hw.status === 'revision' ? 'text-on-primary-container' : 'text-on-surface-variant',
            )}
          >
            {lines.section && (
              <>
                <span className="text-on-surface-variant">{lines.section}</span>
                {' · '}
              </>
            )}
            {/* Вернулась в очередь после доработки — «отправлено заново» (договор §2). */}
            {hw.status === 'pending' && hw.resubmitted ? t('hw.resent') : STATUS_LABEL[hw.status]}
            {' · '}
            {new Date(hw.submitted_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })}
          </span>
        </span>
        {action === 'redo' && redoHref && (
          <Link
            to={redoHref}
            data-hw-redo=""
            className="flex-none rounded-full bg-primary px-4 py-[7px] text-[11px] font-bold text-on-primary transition hover:bg-primary-hover"
          >
            {t('hw.redo')}
          </Link>
        )}
        {/* Окно — только там, где в уроке отвечать негде: `homeworkRowAction`. */}
        {action === 'resubmit' && (
          <button
            type="button"
            onClick={onResubmit}
            className="flex-none rounded-full bg-primary px-4 py-[7px] text-[11px] font-bold text-on-primary transition hover:bg-primary-hover"
          >
            {t('hw.resubmit')}
          </button>
        )}
      </div>

      {queued && onSendQueued && (
        <QueuedRecordLine
          row={queued}
          onSend={() => onSendQueued(queued.id)}
          replaces={hw.status === 'pending' ? 'pending' : 'sent'}
          className="ml-12 mt-2"
        />
      )}

      {/* ⚠️ ОТЗЫВ КУРАТОРА И ФАЙЛЫ ОСТАЮТСЯ ВИДНЫ. В макете строка домашки
          состоит из двух подписей и всё; но там же нет и разбора работы. У нас
          он есть, и прятать его ради вида нельзя: ради этого отзыва ученик сюда
          и заходит. Показываем под строкой, внутри той же полосы. */}
      {(reviewNote || (hw.files && hw.files.length > 0)) && (
        <div className="mt-2 space-y-2 pl-12">
          {/* ⚠️ ОЦЕНКИ И ОЧКОВ ЗДЕСЬ НЕТ (D15, решение владельца 10.09.2026). До
              этого рядом с отзывом стояли «N из 100», «принято без оценки» и
              бонус куратора. За домашку очки больше не начисляются, и число
              обещало бы ученику то, чего нет. Решение куратора видно по
              статусу строки, отзыв — здесь. */}
          {reviewNote && (
            <div className="rounded-[14px] bg-primary-container px-3 py-2 text-[12.5px] text-on-surface">
              <strong className="mb-0.5 block text-[10px] font-bold uppercase tracking-[0.1em] text-on-primary-container">
                {t('hw.curator')}
              </strong>
              {reviewNote}
            </div>
          )}
          {hw.files && hw.files.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {hw.files.map((f) => (
                <a
                  key={f.id}
                  href={f.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 rounded-full bg-surface-container-high px-3 py-1 text-[11.5px] text-on-surface"
                >
                  <span className="material-symbols-outlined text-[14px]">attach_file</span>
                  {f.file_name}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================
// Submit modal — text + file uploads + audio recorder
// =============================================================

function HomeworkSubmitModal({
  open,
  onClose,
  defaultSectionId,
  defaultLessonTitle,
  defaultSectionTitle,
  onSubmitted,
}: {
  open: boolean;
  onClose: () => void;
  defaultSectionId: string;
  defaultLessonTitle: string;
  defaultSectionTitle: string;
  onSubmitted: () => void;
}) {
  const { t } = useTranslation();
  const [sectionId, setSectionId] = useState(defaultSectionId);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<{ name: string; url: string; uploading?: boolean }[]>([]);
  const [audio, setAudio] = useState<{ url: string; blob: Blob } | null>(null);
  const [uploadedAudioURL, setUploadedAudioURL] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Как писалась работа — для куратора, не для ученика.
  const draft = useDraftTelemetry();

  // То же правило подписи, что и в строке списка: урок крупно, раздел ниже и без
  // повтора. Запасное слово здесь своё — раздел мог быть удалён.
  const target = homeworkTitleLines(
    { lesson_title: defaultLessonTitle, section_title: defaultSectionTitle },
    t('hw.sectionUnknown'),
  );

  useEffect(() => {
    if (open) {
      setSectionId(defaultSectionId);
      setContent('');
      setFiles([]);
      setAudio(null);
      setUploadedAudioURL(null);
      setError(null);
      // Форма открылась заново — счётчики начинаются с нуля, иначе вторая
      // домашка унаследует время и вставки первой.
      draft.reset();
    }
  }, [open, defaultSectionId, draft]);

  /**
   * Сколько записи отправлено.
   *
   * 🔴 ЗДЕСЬ ЭТО ВАЖНЕЕ, ЧЕМ В УРОКЕ. В уроке запись ложится в очередь и
   * переживает даже закрытие вкладки; здесь загрузка идёт прямой мутацией —
   * ушёл со страницы, и записи нет. Значит ученик должен видеть, что она
   * действительно идёт, и знать цену ухода.
   */
  const [sendPercent, setSendPercent] = useState<number | null>(null);
  const [sendingAudio, setSendingAudio] = useState(false);
  // Пока идёт загрузка записи, страница спрашивает подтверждение при уходе.
  useEffect(() => (sendingAudio ? unsafeSendStarted() : undefined), [sendingAudio]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!sectionId.trim()) throw new Error(t('hw.sectionRequired'));

      // If we have a local audio blob that hasn't been uploaded yet, push it
      // through files-service first so the final URL goes into file_urls.
      let audioURL = uploadedAudioURL;
      if (audio && !audioURL) {
        setSendingAudio(true);
        setSendPercent(0);
        try {
          const res = await uploadsApi.upload(
            audio.blob,
            'homework_audio',
            `recording-${Date.now()}.webm`,
            setSendPercent,
          );
          audioURL = res.url;
          setUploadedAudioURL(audioURL);
        } finally {
          // ⚠️ Снимать предупреждение обязательно и при отказе: иначе уход со
          // страницы навсегда останется под вопросом, хотя терять уже нечего.
          setSendingAudio(false);
          setSendPercent(null);
        }
      }

      const fileURLs = files.filter((f) => f.url).map((f) => f.url);
      if (audioURL) fileURLs.push(audioURL);

      return progressApi.submitHomework({
        section_id: sectionId.trim(),
        content,
        file_urls: fileURLs,
        draft: draft.snapshot(),
      });
    },
    onSuccess: (...args: Parameters<typeof onSubmitted>) => {
      notifySuccess();
      onSubmitted(...args);
    },
    onError: (e: unknown) => {
      notifyError();
      setError(humanizeError(e));
    },
  });

  const handleFiles = async (chosen: FileList | null) => {
    if (!chosen) return;
    const arr = Array.from(chosen);
    // Optimistically show each file with an `uploading` flag, then patch the
    // URL in once files-service returns. Failures are dropped with a toast-ish
    // inline error rather than blocking the whole modal.
    const initial = arr.map((f) => ({ name: f.name, url: '', uploading: true }));
    setFiles((prev) => [...prev, ...initial]);
    for (let i = 0; i < arr.length; i++) {
      try {
        const res = await uploadsApi.upload(arr[i], 'homework');
        setFiles((prev) => {
          const next = [...prev];
          const idx = next.findIndex((f) => f.name === arr[i].name && f.uploading);
          if (idx !== -1) next[idx] = { name: res.filename, url: res.url };
          return next;
        });
      } catch (e) {
        setError(t('hw.uploadError', { name: arr[i].name, error: humanizeError(e) }));
        setFiles((prev) => prev.filter((f) => !(f.name === arr[i].name && f.uploading)));
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('hw.modalTitle')} size="lg">
      <div className="space-y-4">
        {!defaultSectionId && (
          <div className="rounded-button bg-surface-container-low p-3 text-sm text-on-surface-variant">
            <span className="material-symbols-outlined mr-1 align-middle text-base text-primary">
              info
            </span>
            {t('hw.sectionHint')}
          </div>
        )}
        {/* ⚠️ КОГДА РАЗДЕЛ ПОДСТАВЛЕН, ПОЛЯ ЗДЕСЬ НЕТ ВОВСЕ (10.09.2026). Раньше в нём
            лежал UUID раздела — заблокированный, с подписью «Раздел подставлен из урока».
            То есть ученику показывали технический идентификатор, который ему ничего не
            говорит и который он всё равно не мог изменить. Заказчик прислал снимок.
            Теперь на этом месте — урок и раздел; id по-прежнему уходит на сервер, но на
            экран не попадает. Ручной ввод остаётся только для случая без подстановки. */}
        {defaultSectionId ? (
          <div className="rounded-button bg-surface-container-low px-3.5 py-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
              {t('hw.submittingTo')}
            </p>
            <p className="mt-1 text-sm font-semibold text-on-surface">{target.title}</p>
            {target.section && (
              <p className="text-[12.5px] text-on-surface-variant">{target.section}</p>
            )}
          </div>
        ) : (
          <label className="block text-sm">
            {t('hw.sectionLabel')}
            <Input
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              placeholder={t('hw.sectionPlaceholder')}
              hint={t('hw.sectionManualHint')}
            />
          </label>
        )}

        <label className="block text-sm">
          {t('hw.answerLabel')}
          <textarea
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              draft.onChange(e);
            }}
            onPaste={draft.onPaste}
            rows={5}
            className="mt-1 w-full rounded-button bg-surface-container-lowest px-3 py-2 text-sm text-on-surface shadow-soft ring-1 ring-inset ring-outline-variant/30 transition placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t('hw.answerPlaceholder')}
          />
        </label>

        <div>
          <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-button bg-surface-container-low px-4 py-3 text-sm font-medium text-on-surface-variant transition hover:bg-surface-container-high">
            <span className="material-symbols-outlined text-lg">upload</span>
            {t('hw.filesLabel')}
            <input
              type="file"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
          </label>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center justify-between rounded-button bg-surface-container-low px-3 py-1.5 text-xs"
                >
                  <span className="truncate">{f.name}</span>
                  {f.uploading ? (
                    <Pill variant="warning">{t('hw.uploading')}</Pill>
                  ) : (
                    <button
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="text-error"
                    >
                      <span className="material-symbols-outlined text-base">close</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <AudioRecorder audio={audio} onChange={setAudio} />

        {/*
          ⚠️ ЗДЕСЬ ЗАПИСЬ ТЕРЯЕТСЯ ПО-НАСТОЯЩЕМУ. Загрузка идёт прямой
          мутацией, мимо очереди неотправленного: уход со страницы или
          закрытие вкладки во время отправки стирает её совсем. Поэтому и
          полоса, и предупреждение — в отличие от урока, где очередь всё
          переживает и пугать нечем.
        */}
        {sendingAudio && (
          <div className="space-y-1" role="status">
            <p className="text-xs text-on-surface-variant">
              {sendPercent == null
                ? t('blockRenderers.sendUploadingPlain')
                : t('blockRenderers.sendUploading', { percent: sendPercent })}
            </p>
            {sendPercent != null && (
              <div className="h-1 w-full overflow-hidden rounded-full bg-surface-container-high">
                <div className="h-full bg-primary" style={{ width: `${sendPercent}%` }} />
              </div>
            )}
            <p className="text-[11px] text-on-surface-variant">{t('hw.sendDoNotLeave')}</p>
          </div>
        )}

        {error && <p className="text-sm text-error">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            onClick={() => submit.mutate()}
            loading={submit.isPending}
            disabled={!sectionId.trim() || (!content.trim() && files.length === 0 && !audio)}
          >
            {t('hw.send')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================
// MediaRecorder-backed audio recorder
// =============================================================

function AudioRecorder({
  audio,
  onChange,
}: {
  audio: { url: string; blob: Blob } | null;
  onChange: (a: { url: string; blob: Blob } | null) => void;
}) {
  const { t } = useTranslation();
  const ask = useConfirm();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [micLoss, setMicLoss] = useState<MicLoss | null>(null);
  const [notHeard, setNotHeard] = useState(false);
  const [verdict, setVerdict] = useState<RecordingVerdict | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // ⚠️ ЧАСЫ, А НЕ СЧЁТ ТИКОВ. До 12.09.2026 здесь тикал `setInterval` по секунде:
  // в свёрнутой вкладке таймеры усыпляются, и счётчик отставал от настоящей
  // длины записи. Те же часы, что в уроке, — `lib/recordingClock.ts`.
  const clockRef = useRef(createRecordingClock());
  const stopFlagsRef = useRef<StopFlags>({});
  const stopReasonRef = useRef<StopReason | null>(null);
  const unwatchRef = useRef<(() => void) | null>(null);
  const meterRef = useRef<MicMeter | null>(null);
  const wakeRef = useRef<(() => void) | null>(null);
  const barRef = useRef<(() => void) | null>(null);
  const muteWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const takeRef = useRef(0);

  const clearMuteWait = () => {
    if (muteWaitRef.current) clearTimeout(muteWaitRef.current);
    muteWaitRef.current = null;
  };

  /** Погасить всё, что живёт только во время записи. */
  const stopCompanions = () => {
    clearMuteWait();
    meterRef.current?.stop();
    meterRef.current = null;
    wakeRef.current?.();
    wakeRef.current = null;
    barRef.current?.();
    barRef.current = null;
    unwatchRef.current?.();
    unwatchRef.current = null;
    setNotHeard(false);
  };

  const stop = (by: 'button' | 'leave' | 'loss' = 'button') => {
    if (by === 'button') stopFlagsRef.current.button = true;
    else if (by === 'leave') stopFlagsRef.current.leave = true;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    stopCompanions();
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (rec && rec.state !== 'inactive') rec.stop();
    rec?.stream.getTracks().forEach((track) => track.stop());
    setRecording(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stop('leave'), []);
  useEffect(() => (recording ? recordingStarted() : undefined), [recording]);

  const start = async () => {
    setError(null);
    setMicLoss(null);
    setVerdict(null);
    stopFlagsRef.current = {};
    stopReasonRef.current = null;
    takeRef.current += 1;
    const take = takeRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        clockRef.current.stop(Date.now());
        stopReasonRef.current = pickStopReason(stopFlagsRef.current);
        // ⚠️ Тип берём у рекордера и у кусков, а не зашиваем «webm»: Safari на
        // iPhone пишет mp4, и файл уезжал отсюда с чужим расширением.
        const chunkType = chunksRef.current.find((part) => part.type)?.type;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || chunkType || 'audio/webm' });
        onChange({ url: URL.createObjectURL(blob), blob });
        const clientSeconds = clockRef.current.elapsedMs(Date.now()) / 1000;
        setVerdict(recordingVerdict({ clientSeconds, fileSeconds: null, bytes: blob.size, measured: false }));
        const measured = canMeasureAudio();
        void measureBlobSeconds(blob).then((fileSeconds) => {
          if (takeRef.current !== take) return;
          setVerdict(recordingVerdict({ clientSeconds, fileSeconds, bytes: blob.size, measured }));
        });
      };
      // Те же спутники, что в уроке: слежение за микрофоном, уровень, экран и
      // прилипшая полоска. Иначе домашка осталась бы вторым местом, где запись
      // теряется молча.
      unwatchRef.current = watchMicrophone({
        tracks: stream.getAudioTracks?.() ?? stream.getTracks?.() ?? [],
        recorder: rec,
        devices: navigator.mediaDevices,
        elapsedMs: () => clockRef.current.elapsedMs(Date.now()),
        onLoss: (loss) => {
          setMicLoss(loss);
          stopFlagsRef.current[loss.kind] = true;
          if (loss.kind !== 'mute') {
            stop('loss');
            return;
          }
          clearMuteWait();
          muteWaitRef.current = setTimeout(() => stop('loss'), MUTE_GRACE_MS);
        },
        onBack: () => {
          clearMuteWait();
          setMicLoss(null);
        },
      });
      meterRef.current = attachMicLevel(stream);
      wakeRef.current = keepScreenAwake();
      barRef.current = startBar({
        blockId: 'homework',
        elapsedMs: () => clockRef.current.elapsedMs(Date.now()),
        level: () => meterRef.current?.level() ?? 0,
        state: () => 'recording',
        stop: () => stop('button'),
        onPreempt: () => stop('button'),
      });
      rec.start();
      clockRef.current.start(Date.now());
      recorderRef.current = rec;
      setElapsed(0);
      tickRef.current = setInterval(() => {
        const now = Date.now();
        setElapsed(Math.floor(clockRef.current.elapsedMs(now) / 1000));
        const meter = meterRef.current;
        if (meter) {
          meter.level();
          setNotHeard(meter.silentMs(now) >= SILENCE_ALERT_MS);
        }
      }, 200);
      setRecording(true);
    } catch (e) {
      // Причина отказа разбирается, а не сводится к одной фразе: «разрешите
      // микрофон» бесполезно тому, у кого он занят другим приложением.
      setError(t(micProblemKey(micProblem(e), isStoreBuild()), { brand: APP_DEVICE_NAME }));
    }
  };

  const discard = async () => {
    if (!(await ask({ message: t('blockRenderers.recordDiscardConfirm'), confirmLabel: t('common.delete'), danger: true }))) {
      return;
    }
    takeRef.current += 1;
    setVerdict(null);
    setMicLoss(null);
    stopReasonRef.current = null;
    onChange(null);
  };

  return (
    <div>
      <p className="text-sm font-semibold">{t('hw.voiceAnswer')}</p>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        {!recording ? (
          <Button variant="secondary" onClick={start} type="button">
            <span className="material-symbols-outlined">mic</span>
            {audio ? t('blockRenderers.audioReRecord') : t('hw.record')}
          </Button>
        ) : (
          <Button variant="danger" onClick={() => stop('button')} type="button">
            <span className="material-symbols-outlined">stop</span>
            {t('hw.stopRecording', { elapsed })}
          </Button>
        )}
        {audio && !recording && (
          <>
            <AudioPlayer src={audio.url} className="min-w-[220px] flex-1" />
            <button
              type="button"
              onClick={discard}
              aria-label={t('blockRenderers.recordDiscard')}
              className="grid h-11 w-11 place-items-center text-error"
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
          </>
        )}
      </div>
      {micLoss && recording && (
        <p role="status" className="mt-1 text-xs font-medium text-error">
          {t('blockRenderers.recordMicSilentLive')}
        </p>
      )}
      {!micLoss && notHeard && recording && (
        <p role="status" className="mt-1 text-xs font-medium text-error">
          {t('blockRenderers.recordNotHeard')}
        </p>
      )}
      {!recording && stopReasonRef.current && isUnexpectedStop(stopReasonRef.current) && (
        <p role="alert" className="mt-1 text-xs font-medium text-error">
          {t(stopReasonKey(stopReasonRef.current) as string, {
            time: clock(micLoss ? micLoss.atMs / 1000 : elapsed),
          })}
        </p>
      )}
      {!recording && verdict?.messageKey && (
        <p
          role={verdict.canSave ? 'status' : 'alert'}
          className={cn('mt-1 text-xs font-medium', verdict.canSave ? 'text-on-surface-variant' : 'text-error')}
        >
          {t(verdict.messageKey, verdict.params)}
        </p>
      )}
      {error && <p className="mt-1 text-xs text-error">{error}</p>}
    </div>
  );
}
